import { useMemo } from "react";
import useAPI from "@/services/api/hook";
import { OptionItem } from "../utils/fieldFormUtils";

export interface RawLookupItem {
  id: number | string;
  name: string;
  isHidden?: boolean;
  isSystem?: boolean;
}

interface UseLookupValuesArgs {
  route: string;
  // Parent-scoped lookups only; the fetch is disabled until a parent is chosen.
  parentPathParam?: string;
  parentId?: number | string | null;
  // Pulls the raw rows out of the response (envelope vs bare array).
  selectItems: (data: unknown) => RawLookupItem[];
  enabled?: boolean;
}

/** Generic reader for a lookup field's values; route/scoping/shape come from the registry, rows normalized to OptionItem. */
export const useLookupValues = ({
  route,
  parentPathParam,
  parentId,
  selectItems,
  enabled = true
}: UseLookupValuesArgs): { items: OptionItem[]; isFetching: boolean } => {
  const isScoped = parentPathParam !== undefined;
  const pathParams = isScoped
    ? { [parentPathParam as string]: parentId == null ? "" : String(parentId) }
    : undefined;
  const isEnabled = enabled && (!isScoped || parentId != null);

  const { data, isFetching } = useAPI(
    route,
    { pathParams },
    { enabled: isEnabled }
  );

  const items = useMemo<OptionItem[]>(() => {
    if (!data) return [];
    return selectItems(data).map((raw) => ({
      id: raw.id,
      name: raw.name,
      isHidden: raw.isHidden ?? false,
      isSystem: raw.isSystem ?? false
    }));
  }, [data, selectItems]);

  return { items, isFetching };
};
